/* iter55f85 — Admin Analytics dashboard.
 *
 * Four charts + a conversions section powered by the analytics
 * pipeline in `/api/admin/analytics/*`.
 *
 *   1. Trial → Paid conversion counts + timeline (Line)
 *   2. Panel dwell time by tier — stacked bar
 *   3. Chat-model share over time — one line per model
 *   4. Tools ranked by clicks — horizontal bar
 *   5. Help sections — sortable table with views + avg dwell
 *
 * All data lives in `analytics_events` (30-day default rolling window,
 * adjustable via the top-right window picker).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import { RefreshCw, Loader2, TrendingUp, Users, Sparkles, BookOpen, PieChart, Globe2, Target } from 'lucide-react';
import { adminApi, onboardingApi } from '@/lib/api';
import { PanelHeader } from './_shared';

const WINDOWS = [7, 14, 30, 60, 90];

const TIER_COLORS = {
  trial:   '#94a3b8',
  free:    '#94a3b8',
  basic:   '#5a1c1f',
  pro:     '#c6a664',
  premier: '#0f766e',
};

const MODEL_COLORS = [
  '#5a1c1f', '#c6a664', '#0f766e', '#7c3aed', '#0369a1',
  '#b45309', '#4d7c0f', '#ea580c', '#6b7280',
];

function Card({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <div
      className="rounded-md border border-border bg-card p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            {Icon && <Icon className="h-3.5 w-3.5 text-primary" />} {title}
          </div>
          {subtitle && <div className="mt-0.5 text-[10.5px] text-muted-foreground">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function useWindowedData(fetcher, days) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetcher(days));
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fetcher, days]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);

  const conv    = useWindowedData(adminApi.analyticsConversions, days);
  const landing = useWindowedData(adminApi.analyticsLanding,     days);
  const panels  = useWindowedData(adminApi.analyticsPanels,      days);
  const models  = useWindowedData(adminApi.analyticsModels,      days);
  const tools   = useWindowedData((d) => adminApi.analyticsTools(d, 15), days);
  const help    = useWindowedData(adminApi.analyticsHelp,        days);

  const [onboardStats, setOnboardStats] = useState(null);
  const [onboardLoading, setOnboardLoading] = useState(true);
  const loadOnboard = useCallback(async () => {
    setOnboardLoading(true);
    try { setOnboardStats(await onboardingApi.activationStats()); }
    catch { /* non-critical */ }
    finally { setOnboardLoading(false); }
  }, []);
  useEffect(() => { loadOnboard(); }, [loadOnboard]);

  const reloadAll = () => {
    conv.reload(); landing.reload(); panels.reload(); models.reload(); tools.reload(); help.reload();
    loadOnboard();
  };

  return (
    <div className="space-y-4" data-testid="admin-analytics-panel">
      <PanelHeader
        title="Product Analytics"
        subtitle="What writers use, prefer, and linger on. All figures respect the writer's Prompt-Mode opt-out."
      >
        <div className="flex items-center gap-2">
          <select
            className="h-8 rounded-md border border-border bg-background px-2 text-[12px]"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="analytics-window-picker"
          >
            {WINDOWS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
          <button
            onClick={reloadAll}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11.5px] hover:bg-secondary"
            data-testid="analytics-reload"
          >
            <RefreshCw className="h-3 w-3" /> Reload
          </button>
        </div>
      </PanelHeader>

      {/* Onboarding Activation Funnel */}
      <OnboardingFunnelSection loading={onboardLoading} data={onboardStats} />

      {/* Conversions section */}
      <ConversionsSection loading={conv.loading} data={conv.data} days={days} />

      {/* 2026-02 · Landing funnel — marketing conversion tile */}
      <LandingFunnelSection loading={landing.loading} data={landing.data} days={days} />

      {/* Two-up: panel dwell + model share */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <PanelDwellChart loading={panels.loading} data={panels.data} />
        <ChatModelShareChart loading={models.loading} data={models.data} />
      </div>

      {/* Two-up: tool clicks + help table */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ToolsClicksChart loading={tools.loading} data={tools.data} />
        <HelpSectionsTable loading={help.loading} data={help.data} />
      </div>
    </div>
  );
}

function ConversionsSection({ loading, data, days }) {
  const totals = data?.totals || {};
  const timeline = data?.timeline || [];
  return (
    <Card
      icon={TrendingUp}
      title="Trial → Paid Conversions"
      subtitle={`Last ${days} days · stamped when a trial user completes their first Stripe subscription checkout.`}
      testId="analytics-conversions-card"
    >
      {loading ? <SkeletonRow /> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Trials all-time"            value={totals.trials_all_time || 0}         testId="stat-trials-all"        />
            <Stat label="Paid all-time"              value={totals.conversions_all_time || 0}   testId="stat-conv-all"          />
            <Stat label="All-time rate"              value={`${totals.conversion_rate_all_time ?? 0}%`} testId="stat-rate-all"    />
            <Stat label={`Rate (last ${totals.window_days || days} d)`}
                  value={`${totals.conversion_rate_in_window ?? 0}%`} testId="stat-rate-window" hint={`${totals.conversions_in_window || 0} / ${totals.trials_in_window || 0}`} />
          </div>
          <div className="mt-4 h-56 w-full">
            <ResponsiveContainer>
              <LineChart data={timeline}>
                <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
                <XAxis dataKey="day" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis fontSize={10} allowDecimals={false} />
                <ReTooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="trial_starts"      name="Trials started"   stroke="#94a3b8" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="trial_conversions" name="Trials converted" stroke="#5a1c1f" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * ConfidenceRing — statistical-significance badge next to "Leader: X"
 * in the A/B cohort panel. Tone matches the z-test confidence level:
 *   high  (99%+)  → emerald filled ring
 *   mid   (95%+)  → amber  filled ring
 *   low+  (90%+)  → slate  filled ring
 *   low           → hollow ring (not enough data)
 */
function ConfidenceRing({ confidence }) {
  const level = confidence?.level || 'low';
  const map = {
    high: { color: '#059669', bg: '#059669', label: confidence.label },
    mid:  { color: '#d97706', bg: '#d97706', label: confidence.label },
    'low+': { color: '#475569', bg: '#475569', label: confidence.label },
    low:  { color: '#94a3b8', bg: 'transparent', label: confidence?.label || 'Not enough data yet' },
  };
  const tone = map[level] || map.low;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] normal-case tracking-normal"
      style={{ borderColor: tone.color, color: tone.color }}
      title={confidence?.z ? `z = ${confidence.z.toFixed(2)}` : 'Two-proportion z-test'}
      data-testid="landing-ab-confidence"
      data-level={level}
    >
      <span
        aria-hidden
        className="inline-block h-2 w-2 rounded-full border"
        style={{ borderColor: tone.color, backgroundColor: tone.bg }}
      />
      {tone.label}
    </span>
  );
}



/**
 * 2026-02 · LandingFunnelSection.
 *
 * Renders the public landing → /how-we-help-writers → trial-signup
 * click-through funnel from `/admin/analytics/landing`. Marketing
 * uses this to A/B-test the invitation-card copy without curl-ing
 * the API.
 */
function LandingFunnelSection({ loading, data, days }) {
  const totals = useMemo(() => {
    const rows = data?.totals || [];
    const byEvent = Object.fromEntries(rows.map((r) => [r.event, r]));
    const g = (e) => byEvent[e] || { count: 0, unique_visitors: 0 };
    return {
      landing_view:               g('landing_view'),
      landing_invite_click:       g('landing_invite_click'),
      hwh_view:                   g('hwh_view'),
      hwh_start_trial_click:      g('hwh_start_trial_click'),
      hwh_signin_click:           g('hwh_signin_click'),
      hwh_plans_click:            g('hwh_plans_click'),
      hwh_faq_click:              g('hwh_faq_click'),
    };
  }, [data]);

  const inviteCtr = totals.landing_view.count
    ? ((totals.landing_invite_click.count / totals.landing_view.count) * 100).toFixed(1)
    : '0.0';
  const trialCtr = totals.hwh_view.count
    ? ((totals.hwh_start_trial_click.count / totals.hwh_view.count) * 100).toFixed(1)
    : '0.0';

  // A/B cohort split — each variant's CTR is measured against the
  // total landing_view pool so a lopsided sampling doesn't skew the
  // comparison.
  const ab = data?.invite_ab || { A: { count: 0 }, B: { count: 0 }, unknown: { count: 0 } };
  const abCtrA = totals.landing_view.count
    ? ((ab.A.count / totals.landing_view.count) * 100).toFixed(1) : '0.0';
  const abCtrB = totals.landing_view.count
    ? ((ab.B.count / totals.landing_view.count) * 100).toFixed(1) : '0.0';
  const winner = ab.A.count === ab.B.count
    ? 'tied'
    : ab.A.count > ab.B.count ? 'A' : 'B';

  // Two-proportion z-test — compares invite CTR between variants A
  // and B against the same landing_view denominator. Sample-size
  // guarded (<30 per bucket ⇒ "not enough data") so marketing never
  // celebrates a two-click "win".
  const abConfidence = (() => {
    const n = totals.landing_view.count;
    const x1 = ab.A.count;
    const x2 = ab.B.count;
    if (n < 60 || x1 + x2 < 30) return { level: 'low', label: 'Not enough data yet', z: 0 };
    const p1 = x1 / n;
    const p2 = x2 / n;
    const p  = (x1 + x2) / (2 * n);
    const denom = Math.sqrt(p * (1 - p) * (2 / n));
    if (!denom) return { level: 'low', label: 'Not enough data yet', z: 0 };
    const z = Math.abs(p1 - p2) / denom;
    if (z >= 2.576) return { level: 'high', label: '99% confident', z };
    if (z >= 1.96)  return { level: 'mid',  label: '95% confident', z };
    if (z >= 1.645) return { level: 'low+', label: '90% confident', z };
    return { level: 'low', label: 'Inconclusive — collect more clicks', z };
  })();

  return (
    <Card
      icon={Globe2}
      title="Landing Funnel"
      subtitle={`Last ${days} days · public landing → How-We-Help → trial-signup click-through.`}
      testId="analytics-landing-funnel-card"
    >
      {loading ? <SkeletonRow /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Landing views"
                  value={totals.landing_view.count}
                  hint={`${totals.landing_view.unique_visitors} unique`}
                  testId="landing-stat-views" />
            <Stat label="Invite clicks"
                  value={totals.landing_invite_click.count}
                  hint={`CTR ${inviteCtr}%`}
                  testId="landing-stat-invite-clicks" />
            <Stat label="/how-we-help views"
                  value={totals.hwh_view.count}
                  hint={`${totals.hwh_view.unique_visitors} unique`}
                  testId="landing-stat-hwh-views" />
            <Stat label="Trial CTA clicks"
                  value={totals.hwh_start_trial_click.count}
                  hint={`CTR ${trialCtr}%`}
                  testId="landing-stat-trial-clicks" />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <Stat label="Sign-in clicks"       value={totals.hwh_signin_click.count} testId="landing-stat-signin-clicks" />
            <Stat label="Plans-dialog clicks"  value={totals.hwh_plans_click.count}  testId="landing-stat-plans-clicks" />
            <Stat label="FAQ clicks"           value={totals.hwh_faq_click.count}    testId="landing-stat-faq-clicks" />
          </div>

          {/* A/B cohort split — invite-card headline variants */}
          <div className="rounded-md border border-border/60 bg-background/60 p-3"
                data-testid="landing-ab-cohort">
            <div className="mb-2 flex items-center justify-between text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground">
              <span>Invitation-card A/B split</span>
              <span className="flex items-center gap-2">
                <span data-testid="landing-ab-winner">
                  {winner === 'tied' ? 'Tied' : `Leader: ${winner}`}
                </span>
                <ConfidenceRing confidence={abConfidence} />
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div
                className={`rounded-sm border p-3 ${winner === 'A' ? 'border-primary' : 'border-border'}`}
                data-testid="landing-ab-variant-a"
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Variant A</div>
                <div className="mt-1 font-mono-stat text-[20px] leading-none text-foreground">{ab.A.count}</div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">
                  CTR {abCtrA}% · {ab.A.unique_visitors || 0} unique
                </div>
              </div>
              <div
                className={`rounded-sm border p-3 ${winner === 'B' ? 'border-primary' : 'border-border'}`}
                data-testid="landing-ab-variant-b"
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Variant B</div>
                <div className="mt-1 font-mono-stat text-[20px] leading-none text-foreground">{ab.B.count}</div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">
                  CTR {abCtrB}% · {ab.B.unique_visitors || 0} unique
                </div>
              </div>
            </div>
            {ab.unknown.count > 0 && (
              <div className="mt-2 text-[10.5px] text-muted-foreground"
                   data-testid="landing-ab-unknown-note">
                {ab.unknown.count} click{ab.unknown.count === 1 ? '' : 's'} arrived without a variant stamp
                (pre-A/B deploy or a client that blocked sessionStorage).
              </div>
            )}
          </div>
          {(data?.timeline || []).length > 0 && (
            <div className="h-56 w-full">
              <ResponsiveContainer>
                <LineChart data={data.timeline}>
                  <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
                  <XAxis dataKey="day" fontSize={10} tickFormatter={(d) => (d || '').slice(5)} />
                  <YAxis fontSize={10} allowDecimals={false} />
                  <ReTooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="landing_view"          name="Landing views"       stroke="#94a3b8" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="landing_invite_click"  name="Invite clicks"       stroke="#c6a664" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="hwh_view"              name="/how-we-help views"  stroke="#0f766e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="hwh_start_trial_click" name="Trial CTA clicks"    stroke="#5a1c1f" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, hint, testId }) {
  return (
    <div className="rounded-sm border border-border bg-background p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono-stat text-[20px] leading-none text-foreground">{value}</div>
      {hint && <div className="mt-1 text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function PanelDwellChart({ loading, data }) {
  const rows = useMemo(() => data?.rows || [], [data]);
  const tiers = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => Object.keys(r.tiers || {}).forEach((t) => s.add(t)));
    return [...s];
  }, [rows]);
  const flattened = rows.slice(0, 12).map((r) => ({
    panel: r.panel,
    ...tiers.reduce((acc, t) => ({ ...acc, [t]: r.tiers[t] || 0 }), {}),
  }));

  return (
    <Card
      icon={Users}
      title="Panel Dwell Time by Tier"
      subtitle="Seconds spent per panel, stacked by subscription tier. Where writers actually live."
      testId="analytics-panels-card"
    >
      {loading ? <SkeletonRow /> : (
        rows.length === 0 ? <EmptyRow msg="No panel focus events yet." /> : (
          <div className="h-64 w-full">
            <ResponsiveContainer>
              <BarChart data={flattened} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
                <XAxis type="number" fontSize={10} />
                <YAxis type="category" dataKey="panel" fontSize={10} width={100} />
                <ReTooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {tiers.map((t) => (
                  <Bar key={t} dataKey={t} name={t} stackId="dwell"
                        fill={TIER_COLORS[t] || '#6b7280'} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )
      )}
    </Card>
  );
}

function ChatModelShareChart({ loading, data }) {
  const models = data?.models || [];
  const series = data?.series || [];
  const byTier = data?.by_tier || {};

  return (
    <Card
      icon={Sparkles}
      title="Chat-Model Share"
      subtitle="Which LLMs writers pick, per day. Model colors are stable across the whole panel."
      testId="analytics-models-card"
    >
      {loading ? <SkeletonRow /> : (
        models.length === 0 ? <EmptyRow msg="No chat-model selections yet." /> : (
          <>
            <div className="h-56 w-full">
              <ResponsiveContainer>
                <LineChart data={series}>
                  <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
                  <XAxis dataKey="day" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                  <YAxis fontSize={10} allowDecimals={false} />
                  <ReTooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {models.map((m, i) => (
                    <Line key={m} type="monotone" dataKey={m} stroke={MODEL_COLORS[i % MODEL_COLORS.length]}
                          strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-4">
              {Object.entries(byTier).map(([tier, counts]) => {
                const total = Object.values(counts).reduce((a, b) => a + b, 0);
                const winner = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
                return (
                  <div key={tier} className="rounded-sm border border-border bg-background p-2" data-testid={`analytics-tier-preference-${tier}`}>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tier}</div>
                    <div className="mt-0.5 truncate font-medium text-foreground">{winner?.[0] || '—'}</div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">{winner ? `${winner[1]}/${total}` : ''}</div>
                  </div>
                );
              })}
            </div>
          </>
        )
      )}
    </Card>
  );
}

function ToolsClicksChart({ loading, data }) {
  const rows = (data?.rows || []).map((r) => ({ tool: r.tool, clicks: r.clicks }));
  return (
    <Card
      icon={PieChart}
      title="Tools Ranked by Clicks"
      subtitle="Top 15 writer-facing tools by how often they're clicked."
      testId="analytics-tools-card"
    >
      {loading ? <SkeletonRow /> : (
        rows.length === 0 ? <EmptyRow msg="No tool-click events yet." /> : (
          <div className="h-64 w-full">
            <ResponsiveContainer>
              <BarChart data={rows} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
                <XAxis type="number" fontSize={10} />
                <YAxis type="category" dataKey="tool" fontSize={10} width={110} />
                <ReTooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="clicks" fill="#5a1c1f" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )
      )}
    </Card>
  );
}

function HelpSectionsTable({ loading, data }) {
  const rows = data?.rows || [];
  return (
    <Card
      icon={BookOpen}
      title="Help Sections Read"
      subtitle="Views and avg. dwell (sec). High views + low dwell = probably too dense."
      testId="analytics-help-card"
    >
      {loading ? <SkeletonRow /> : (
        rows.length === 0 ? <EmptyRow msg="No help views yet." /> : (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-panel-header/70 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">Section</th>
                  <th className="p-2 text-right">Views</th>
                  <th className="p-2 text-right">Avg. dwell</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.section} className="border-t border-border/60" data-testid={`analytics-help-row-${r.section}`}>
                    <td className="p-2">{r.section}</td>
                    <td className="p-2 text-right font-mono-stat">{r.views}</td>
                    <td className="p-2 text-right font-mono-stat">{r.avg_dwell_s.toFixed(1)}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Card>
  );
}

function SkeletonRow() {
  return (
    <div className="flex h-32 items-center justify-center text-muted-foreground">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
    </div>
  );
}
function EmptyRow({ msg }) {
  return (
    <div className="flex h-32 items-center justify-center text-[12px] text-muted-foreground">
      {msg}
    </div>
  );
}


// ─── Onboarding Activation Funnel ─────────────────────────────────────────────
function OnboardingFunnelSection({ loading, data }) {
  const steps        = data?.step_funnel || [];
  const total        = data?.total_pro_studio_users || 0;
  const fullyActive  = data?.fully_activated_count || 0;
  const activationRate = data?.activation_rate_pct ?? (total > 0 ? Math.round((fullyActive / total) * 100) : 0);

  return (
    <Card icon={Target} title="Week 1 Activation Funnel" subtitle="% of Pro/Studio users who completed each onboarding step" testId="analytics-onboarding-funnel">
      {loading ? (
        <SkeletonRow />
      ) : steps.length === 0 ? (
        <EmptyRow msg="No activation data yet — onboarding wizard needed for Pro/Studio users." />
      ) : (
        <div className="space-y-3">
          {/* Summary row */}
          <div className="flex items-center gap-4 text-[12px] pb-2 border-b border-border">
            <span className="text-muted-foreground">Total Pro/Studio users: <strong>{total}</strong></span>
            <span className="text-muted-foreground">Fully activated: <strong className="text-emerald-600">{fullyActive}</strong></span>
            <span className="ml-auto font-semibold text-primary">{activationRate}% activation rate</span>
          </div>
          {/* Step bars */}
          {steps.map((step) => {
            const pct = total > 0 ? Math.round((step.completed_count / total) * 100) : 0;
            return (
              <div key={step.step_id} className="space-y-1">
                <div className="flex items-center justify-between text-[11.5px]">
                  <span className="text-muted-foreground">{step.label}</span>
                  <span className="font-semibold tabular-nums">
                    {step.completed_count} / {total} ({pct}%)
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: pct >= 75 ? 'var(--primary)' : pct >= 40 ? '#f59e0b' : '#ef4444',
                    }}
                  />
                </div>
              </div>
            );
          })}
          <p className="text-[10.5px] text-muted-foreground pt-1">
            Drop-off between steps shows where users need more guidance or a stronger nudge.
          </p>
        </div>
      )}
    </Card>
  );
}
